import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { SaveIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/newRedesign'
import { Button } from '@/components/newRedesign'
import { Form } from '@/components/ui/form'
import { FormSearchableSelect } from '@/components/newRedesign/form'
import { FormCurrencyInput } from '@/components/newRedesign/form'
import { FormTextarea } from '@/components/newRedesign/form'
import { FormSelect } from '@/components/newRedesign/form'
import type { SearchableSelectOption } from '@/components/newRedesign'
import { getCostCategories } from '@/api/costCategoriesAPI'
import { getCurrencies } from '@/api/currenciesAPI'

interface CategoryItem {
  id: number
  name: string
  description?: string
}

export interface CostFormData {
  id?: number
  category: number
  cost: {
    amount: number | undefined
    codeCurrency: string
  }
  type: string
  observations?: string
}

interface CostDialogProps {
  open: boolean
  onOpenChange: (_open: boolean) => void
  onSave: (_data: CostFormData) => void
  onCancel: () => void
  isPending?: boolean
  mode?: 'create' | 'edit'
  initialData?: Partial<CostFormData>
  defaultCategory?: CategoryItem
  showObservations?: boolean
  costTypes?: { id: string; title: string }[]
}

const DEFAULT_COST_TYPES = [
  { id: 'SUPPLIER', title: 'Proveedor' },
  { id: 'OPERATIONAL', title: 'Operacional' },
  { id: 'INDIRECT', title: 'Indirecto' },
  { id: 'UNDEFINED', title: 'Indefinido' },
]

export function CostDialog({
  open,
  onOpenChange,
  onSave,
  isPending = false,
  mode = 'create',
  initialData,
  defaultCategory,
  showObservations = true,
  costTypes = DEFAULT_COST_TYPES,
}: CostDialogProps) {
  const form = useForm<CostFormData>({
    defaultValues: {
      category: undefined,
      cost: {
        amount: undefined,
        codeCurrency: '',
      },
      type: '',
      observations: '',
    },
  })

  // Fetcher para categorías
  const fetchCategories = async (
    search: string
  ): Promise<SearchableSelectOption[]> => {
    const response = await getCostCategories(search ? { search } : undefined)
    if (!Array.isArray(response)) return []
    return response.map(({ id, name }) => ({
      id,
      label: name,
    }))
  }

  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: getCurrencies,
    staleTime: 5 * 60 * 1000,
  })

  // Update form when dialog opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        // Load data for editing
        form.reset({
          id: initialData.id,
          category: initialData.category || undefined,
          cost: {
            amount: initialData.cost?.amount,
            codeCurrency: initialData.cost?.codeCurrency || '',
          },
          type: initialData.type || '',
          observations: initialData.observations || '',
        })
      } else {
        // Reset to empty values for creating new cost
        form.reset({
          category: undefined,
          cost: {
            amount: undefined,
            codeCurrency: '',
          },
          type: '',
          observations: '',
        })
      }
    }
  }, [open, mode, initialData, form])

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset()
    }
  }, [open, form])

  const handleSubmit = (data: CostFormData) => {
    onSave({
      ...data,
      ...(mode === 'edit' && initialData?.id && { id: initialData.id }),
    })
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] w-full max-w-[95vw] flex-col',
          'overflow-hidden bg-[#FAFAFA] p-0',
          'sm:max-w-[90vw] md:max-w-[800px]'
        )}
      >
        <DialogHeader
          className={cn(
            'flex h-[52px] shrink-0 flex-row items-center justify-between',
            'border-b border-[#D4D4D4] px-4 py-0'
          )}
        >
          <DialogTitle className="text-xl font-semibold leading-[24px] tracking-[-0.02em] text-[#262626]">
            {mode === 'edit' ? 'Editar Costo' : 'Nuevo Costo'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <div className="flex max-h-[calc(85vh-120px)] flex-1 flex-col gap-4 overflow-y-auto bg-[#FAFAFA] px-4 py-4">
            {/* Category Selection */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Categoría <span className="text-[#262626]">(*)</span>
              </label>
              <FormSearchableSelect
                control={form.control}
                defaultValue={defaultCategory}
                emptyMessage="No se encontraron categorías"
                fetcher={fetchCategories}
                name="category"
                placeholder="Seleccione una categoría"
                rules={{
                  required: 'La categoría es requerida',
                }}
                searchPlaceholder="Buscar categoría..."
                showLabel={false}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Costo <span className="text-[#262626]">(*)</span>
              </label>
              <FormCurrencyInput
                control={form.control}
                currenciesData={
                  currencies?.map(({ code }) => ({
                    code: code,
                    displayName: code,
                  })) || []
                }
                name="cost"
                placeholder="0.00"
                rules={{
                  required: 'El costo es requerido',
                  validate: (value: {
                    amount?: number
                    codeCurrency: string
                  }) => {
                    const errors: string[] = []

                    if (
                      value?.amount === undefined ||
                      value?.amount === null ||
                      value.amount <= 0
                    ) {
                      errors.push('El monto debe ser mayor a 0')
                    }

                    if (
                      !value?.codeCurrency ||
                      value.codeCurrency.trim() === ''
                    ) {
                      errors.push('La moneda es requerida')
                    }

                    if (value?.amount !== undefined && value.amount > 0) {
                      const amountStr = value.amount.toString()
                      const decimalPart = amountStr.split('.')[1]
                      if (decimalPart && decimalPart.length > 10) {
                        errors.push(
                          'El monto no puede tener más de 10 decimales'
                        )
                      }
                    }

                    return errors.length === 0 || errors.join('. ')
                  },
                }}
                showLabel={false}
              />
            </div>

            {/* Cost Type */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Tipo <span className="text-[#262626]">(*)</span>
              </label>
              <FormSelect
                control={form.control}
                data={costTypes.map((type) => ({
                  id: type.id,
                  title: type.title,
                }))}
                name="type"
                placeholder="Seleccione un tipo"
                rules={{ required: 'El tipo de costo es requerido' }}
                showLabel={false}
              />
            </div>

            {/* Observations (Optional) */}
            {showObservations && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                  Observaciones
                </label>
                <FormTextarea
                  control={form.control}
                  name="observations"
                  placeholder="Observaciones"
                  showLabel={false}
                />
              </div>
            )}
          </div>

          <DialogFooter
            className={cn(
              'flex h-auto shrink-0 items-center justify-end',
              'border-t border-[#D4D4D4] px-3 py-3',
              'sm:h-[68px] sm:px-4 sm:py-0'
            )}
          >
            <Button
              className={cn(
                'flex h-9 w-full items-center justify-center',
                'gap-2 rounded-md bg-[#1E293B] px-4',
                'hover:bg-[#334155]',
                'sm:w-auto sm:min-w-[110px]'
              )}
              disabled={isPending}
              icon={<SaveIcon className="h-4 w-4" />}
              onClick={form.handleSubmit(handleSubmit)}
              type="button"
              variant="default"
            >
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
